
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { SystemUserInterface, SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { CreateSystemUserForm } from '@/services/SystemUsers/forms/CreateSystemUserForm';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { FormValidationFunction } from '@/views/interfaces/vuetify/form.interface';
import { PasswordValidationCallbacks } from '@/services/misc/functions/ValidationFunctions';
import WebsiteGroupRepositoryInterface from '@/services/WebsiteGroup/interfaces';
import { PlanInterface, PlanRepositoryInterface } from '@/services/Plan/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'systemUserRepository',
    'serverRepository',
    'websiteRepository',
    'websiteGroupRepository',
    'planRepository',
  ],
  components: {
    PageHeader,
  },
})
export default class CreateSystemUser extends Vue {
  private loading: boolean = true;
  private form: WPXFormValue<CreateSystemUserForm> = new WPXFormValue(CreateSystemUserForm);
  private menu: string | false = false;

  private systemUserRepository!: SystemUserRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  private planRepository!: PlanRepositoryInterface;

  private servers: ServerInterface[] = [];
  private websites: Website[] = [];
  private plan!: PlanInterface;

  private quotaMultipliers: { name: string, value: number }[] = [
    { name: 'KiB', value: 1 },
    { name: 'MiB', value: Math.pow(1024, 1) },
    { name: 'GiB', value: Math.pow(1024, 2) },
  ];

  public _websiteSearch: string = '';
  public websiteSearchLoading: boolean = false;

  public async created(): Promise<void> {
    try {
      this.servers = await this.serverRepository.getAllServers();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  private async createSystemUser(): Promise<void> {
    try {
      await this.systemUserRepository.createSystemUser({
        name: this.form.data.name,
        password: this.form.data.password,
        passwordConf: this.form.data.passwordConf,
        server: this.form.data.server,
        websiteId: this.form.data.websiteId,
        directory: this.form.data.directory,
        quota: {
          size: this.form.data.quota * this.form.data.quotaMultiplier,
        },
        expireDate: this.form.data.expireDate,
        plan: this.plan,
      });
      this.$successDialog.show('System User Created Successfully');
      await this.$router.push({ name: 'system_users_list' });
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public get websiteSearch(): string {
    return this._websiteSearch;
  }

  public set websiteSearch(val: string) {
    if (val && val.length < 3) {
      return;
    }

    this._websiteSearch = `server=${this.form.data.server}&domain=${val}`;
    this.websiteSearchLoading = true;
    this.websiteRepository.getByServerAndDomain(this.form.data.server, val).then((websites) => {
      this.websites = websites;
      this.websiteSearchLoading = false;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });
  }

  public createDirectory(): void {
    if (!this.form.data.name) {
      this.form.data.directory = '';
      return;
    }

    this.form.data.directory = `/home/${this.form.data.name}`;
  }


  public async loadGroup(websiteId: number): Promise<void> {
    try {
      const group = await this.websiteGroupRepository.getGroupByWebsite(websiteId);
      if (!group) {
        throw new Error(`Group for website with id: ${websiteId} not found!`);
      }

      const plan = await this.planRepository.getOneById(group.plan);
      if (!plan) {
        throw new Error(`Plan with id: ${websiteId} not found!`);
      }

      this.plan = plan;
    } catch (err) {
      this.$errorDialog.show('Failed to load website group and plan for system user creation');
    }
  }

  private rules: { [key: string]: FormValidationFunction[] } = {
    name: [
      (v: string) => !!v || 'Name is required',
      (v: string) => v.length <= 32 || 'Name can be at most 32 characters',
      (v: string) => v.length >= 3 || 'Name has to be at least 3 characters',
      (v: string) => !v || /^[a-zA-Z0-9]+$/.test(v) || 'Name must contain only letters and numbers',
    ],
    server: [
      (v: string) => !!v || 'Server is required',
    ],
    directory: [
      (v: string) => !!v || 'Directory is required',
    ],
    quota: [
      (v: number) => !!v || 'Quota is required',
      (v: number) => v >= 0 || 'Quota must be equal or more than 0',
      (v: number) => {
        const quotaLimit = 200 * Math.pow(1024, 2);
        const currentQuota = v * this.form.data.quotaMultiplier;
        return currentQuota <= quotaLimit || `Quota can not exceed ${quotaLimit / this.form.data.quotaMultiplier}`;
      },
    ],
    websiteId: [
      (v: number) => !!v || 'WebsiteId is required',
      (v: number) => v >= 0 || 'Website must be equal or more than 0',
    ],
    password: [
      (v: string) => !!v || 'Password is required',
      ...PasswordValidationCallbacks,
    ],
  };
}
